import { createApi } from '@reduxjs/toolkit/query/react';

import { apiBaseUrl } from '../Api';
import { makeBaseQuery } from '../baseQuery';
import type {
  BuyerAlertsPreview,
  BuyerDashboardStats,
  BuyerDocumentFileArgs,
  BuyerProfileResult,
  BuyerProfileStats,
  BuyerProfileUser,
  BuyerPropertyBase,
  BuyerPropertyDetail,
  BuyerPropertyDocument,
  BuyerPropertyListItem,
  BuyerPropertyPage,
  BuyerPropertyQueryArgs,
  BuyerRecord,
  UpdateBuyerProfileArgs
} from './buyerTypes';

/**
 * Everything under `/api/buyer/*` that is not an alert, an enquiry or a negotiation: the
 * dashboard, the buyer's own profile, their notifications, and the property browse/saved pair.
 * They are one slice because they are one backend prefix and they invalidate each other —
 * saving a property changes the browse row, the saved list and the personalised feed at once.
 *
 * Envelope handling: these endpoints answer `{ success, message: 'OK', data }` for reads but
 * `{ success, message }` with NO `data` key for the write acknowledgements (`okMessage` on the
 * backend). Every normalizer below is module-scope, takes `unknown` and is TOTAL — a throwing
 * normalizer takes out the route's error boundary instead of showing the query's error state.
 */
export const asRecord = (value: unknown): BuyerRecord =>
  typeof value === 'object' && value !== null ? (value as BuyerRecord) : {};

const envelope = (response: unknown): BuyerRecord => asRecord(asRecord(response).data);

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNullableString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

const asNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return fallback;
};

/** An id the UI hands back to the API is never invented — absent means `null`, not a placeholder. */
const asId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
};

/** `area`/`asking_price` come back as MySQL DECIMAL strings on some rows and numbers on others. */
const asLoose = (value: unknown): string | number | null =>
  typeof value === 'string' || typeof value === 'number' ? value : null;

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

/**
 * Absorbs every list shape these endpoints use: `data: { items: [...] }` (the common one) and a
 * bare `data: [...]`. Anything else is an empty list, never a throw.
 */
export const normalizeItems = (response: unknown): BuyerRecord[] => {
  const data = asRecord(response).data;
  const items = Array.isArray(data) ? data : asRecord(data).items;
  return Array.isArray(items) ? items.map(asRecord) : [];
};

export function normalizeDashboardStats(response: unknown): BuyerDashboardStats {
  const data = envelope(response);
  return {
    savedCount: asNumber(data.savedCount),
    enquiriesCount: asNumber(data.enquiriesCount),
    approvedEnquiries: asNumber(data.approvedEnquiries),
    negotiationsActive: asNumber(data.negotiationsActive),
    viewsCount: asNumber(data.viewsCount),
    newMatchesCount: asNumber(data.newMatchesCount),
    hasAdminUpdate: data.hasAdminUpdate === true,
    profile: data.profile === null || data.profile === undefined ? null : asRecord(data.profile)
  };
}

export function normalizeAlertsPreview(response: unknown): BuyerAlertsPreview {
  return { activeAlerts: asNumber(envelope(response).activeAlerts) };
}

function propertyBaseFields(raw: BuyerRecord): BuyerPropertyBase {
  return {
    id: asNumber(raw.id),
    title: asString(raw.title),
    district: asNullableString(raw.district),
    state: asNullableString(raw.state),
    land_type: asNullableString(raw.land_type),
    area: asLoose(raw.area),
    area_unit: asNullableString(raw.area_unit),
    asking_price: asLoose(raw.asking_price),
    price_unit: asNullableString(raw.price_unit),
    is_negotiable: raw.is_negotiable === true,
    urgency: asNullableString(raw.urgency),
    status: asNullableString(raw.status),
    lawyer_review_status: asNullableString(raw.lawyer_review_status),
    riskLevel: asString(raw.riskLevel, 'low'),
    lawyerVerified: raw.lawyerVerified === true,
    photos: asStringArray(raw.photos),
    videos: asStringArray(raw.videos),
    youtubeVideoIds: asStringArray(raw.youtubeVideoIds),
    created_at: asNullableString(raw.created_at),
    updated_at: asNullableString(raw.updated_at)
  };
}

/**
 * A browse/feed/saved row: the public shape plus the buyer's own save + enquiry state. `...raw`
 * comes first so every column the backend adds later survives, and the typed fields overwrite it.
 */
function normalizePropertyListItem(value: unknown): BuyerPropertyListItem {
  const raw = asRecord(value);
  const enquiry = asRecord(raw.enquiry);
  const enquiryId = asId(enquiry.id);
  return {
    ...raw,
    ...propertyBaseFields(raw),
    saved: raw.saved === true,
    enquiry: enquiryId === null ? null : { id: enquiryId, status: asString(enquiry.status) }
  };
}

export function normalizePropertyList(response: unknown): BuyerPropertyListItem[] {
  return normalizeItems(response).map(normalizePropertyListItem);
}

export function normalizePropertyPage(response: unknown): BuyerPropertyPage {
  const data = envelope(response);
  const items = normalizePropertyList(response);
  return {
    items,
    total: asNumber(data.total, items.length),
    page: asNumber(data.page, 1),
    limit: asNumber(data.limit, items.length),
    totalPages: asNumber(data.totalPages, items.length > 0 ? 1 : 0)
  };
}

export function normalizePropertyDetail(response: unknown): BuyerPropertyDetail {
  const data = envelope(response);
  const legal = asRecord(data.legalStatus);
  const hasProperty = typeof data.property === 'object' && data.property !== null;
  const rawProperty = asRecord(data.property);
  return {
    property: hasProperty
      ? {
          ...rawProperty,
          ...propertyBaseFields(rawProperty),
          survey_number_masked: asNullableString(rawProperty.survey_number_masked)
        }
      : null,
    saved: data.saved === true,
    enquiry: data.enquiry === null || data.enquiry === undefined ? null : asRecord(data.enquiry),
    legalStatus: {
      legalOpinion: asString(legal.legalOpinion, 'pending'),
      documentsVerified: asNumber(legal.documentsVerified),
      documentsTotal: asNumber(legal.documentsTotal),
      encumbrancesActive: asNumber(legal.encumbrancesActive),
      ownershipPersonCount: asNumber(legal.ownershipPersonCount)
    }
  };
}

export function normalizeProfile(response: unknown): BuyerProfileResult {
  const data = envelope(response);
  const rawUser = asRecord(data.user);
  const userId = asId(rawUser.id);
  const stats = asRecord(data.stats);
  const user: BuyerProfileUser | null =
    userId === null
      ? null
      : {
          id: userId,
          name: asString(rawUser.name),
          email: asString(rawUser.email),
          mobile: asString(rawUser.mobile),
          emailVerified: rawUser.emailVerified === true
        };
  const shapedStats: BuyerProfileStats = {
    views: asNumber(stats.views),
    saved: asNumber(stats.saved),
    enquiries: asNumber(stats.enquiries),
    approved: asNumber(stats.approved)
  };
  return {
    user,
    profile: data.profile === null || data.profile === undefined ? null : asRecord(data.profile),
    stats: shapedStats
  };
}

/** `okMessage` answers `{ success, message }` with no `data`, so only the message is available. */
export function normalizeMessage(response: unknown, fallback = ''): string {
  return asString(asRecord(response).message, fallback);
}

function normalizeDocument(value: unknown): BuyerPropertyDocument {
  const raw = asRecord(value);
  return {
    id: asNumber(raw.id),
    document_type: asString(raw.document_type),
    file_name: asNullableString(raw.file_name),
    status: asString(raw.status),
    uploaded_at: asString(raw.uploaded_at)
  };
}

export function normalizeDocumentList(response: unknown): BuyerPropertyDocument[] {
  const data = envelope(response);
  const items = Array.isArray(data.items) ? data.items : [];
  return items.map(normalizeDocument);
}

export const buyerApi = createApi({
  reducerPath: 'buyerApi',
  baseQuery: makeBaseQuery(apiBaseUrl),
  tagTypes: [
    'DashboardStats',
    'Property',
    'SavedProperty',
    'Notification',
    'Profile',
    'Alert',
    'PropertyDocument'
  ],
  endpoints: (builder) => ({
    /* ---- dashboard ---- */
    getBuyerDashboardStats: builder.query<BuyerDashboardStats, void>({
      query: () => ({ url: 'buyer/dashboard/stats' }),
      transformResponse: (response: unknown) => normalizeDashboardStats(response),
      providesTags: [{ type: 'DashboardStats', id: 'STATS' }]
    }),
    getBuyerDashboardActivity: builder.query<BuyerRecord[], void>({
      query: () => ({ url: 'buyer/dashboard/activity' }),
      transformResponse: (response: unknown) => normalizeItems(response),
      providesTags: [{ type: 'DashboardStats', id: 'ACTIVITY' }]
    }),
    getBuyerPersonalizedFeed: builder.query<BuyerPropertyListItem[], void>({
      query: () => ({ url: 'buyer/dashboard/personalized-feed' }),
      transformResponse: (response: unknown) => normalizePropertyList(response),
      providesTags: [{ type: 'Property', id: 'FEED' }]
    }),
    getBuyerDashboardNotifications: builder.query<BuyerRecord[], void>({
      query: () => ({ url: 'buyer/dashboard/notifications' }),
      transformResponse: (response: unknown) => normalizeItems(response),
      providesTags: [{ type: 'Notification', id: 'DASHBOARD' }]
    }),
    /**
     * Lives on the dashboard prefix, not `/buyer/alerts` — it is the alert counter the dashboard
     * shows. `alertApi` invalidates it across slices whenever an alert changes.
     */
    getBuyerAlertsPreview: builder.query<BuyerAlertsPreview, void>({
      query: () => ({ url: 'buyer/dashboard/alerts-preview' }),
      transformResponse: (response: unknown) => normalizeAlertsPreview(response),
      providesTags: [{ type: 'Alert', id: 'PREVIEW' }]
    }),

    /* ---- profile ---- */
    getBuyerProfile: builder.query<BuyerProfileResult, void>({
      query: () => ({ url: 'buyer/profile' }),
      transformResponse: (response: unknown) => normalizeProfile(response),
      providesTags: [{ type: 'Profile', id: 'ME' }]
    }),
    updateBuyerProfile: builder.mutation<string, UpdateBuyerProfileArgs>({
      query: (body) => ({ url: 'buyer/profile', method: 'PUT', body }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: [{ type: 'Profile', id: 'ME' }]
    }),
    updateBuyerPreferences: builder.mutation<string, BuyerRecord>({
      query: (body) => ({ url: 'buyer/profile/preferences', method: 'PUT', body }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      /** The personalised feed IS the preferences, re-queried — leaving it cached shows the old match set. */
      invalidatesTags: [
        { type: 'Profile', id: 'ME' },
        { type: 'Property', id: 'FEED' }
      ]
    }),

    /* ---- browse + saved ---- */
    listBuyerProperties: builder.query<BuyerPropertyPage, BuyerPropertyQueryArgs>({
      query: (params) => ({ url: 'buyer/properties', params }),
      transformResponse: (response: unknown) => normalizePropertyPage(response),
      providesTags: [{ type: 'Property', id: 'LIST' }]
    }),
    getBuyerPropertyDetail: builder.query<BuyerPropertyDetail, { id: number }>({
      query: ({ id }) => ({ url: `buyer/properties/${id}` }),
      transformResponse: (response: unknown) => normalizePropertyDetail(response),
      providesTags: (_result, _error, arg) => [{ type: 'Property', id: String(arg.id) }]
    }),
    listSavedProperties: builder.query<BuyerPropertyListItem[], BuyerPropertyQueryArgs>({
      query: (params) => ({ url: 'buyer/saved', params }),
      transformResponse: (response: unknown) => normalizePropertyList(response),
      providesTags: [{ type: 'SavedProperty', id: 'LIST' }]
    }),
    /**
     * `saved` rides on every browse/feed row, so a save invalidates the lists, not just the
     * detail. `onQueryStarted` flips the cached `saved` flag on every cached page/args
     * combination of those three endpoints immediately, so the heart updates before the
     * invalidation round trip lands, not after it — rolled back on failure.
     */
    saveProperty: builder.mutation<string, { propertyId: number }>({
      query: ({ propertyId }) => ({ url: `buyer/saved/${propertyId}`, method: 'POST' }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'SavedProperty', id: 'LIST' },
        { type: 'Property', id: String(arg.propertyId) },
        { type: 'Property', id: 'LIST' },
        { type: 'Property', id: 'FEED' }
      ],
      onQueryStarted: async ({ propertyId }, { dispatch, getState, queryFulfilled }) => {
        const state = getState();
        const patches = [
          ...buyerApi.util.selectCachedArgsForQuery(state, 'listBuyerProperties').map((args) =>
            dispatch(
              buyerApi.util.updateQueryData('listBuyerProperties', args, (draft) => {
                const item = draft.items.find((i) => i.id === propertyId);
                if (item) item.saved = true;
              })
            )
          ),
          ...buyerApi.util.selectCachedArgsForQuery(state, 'listSavedProperties').map((args) =>
            dispatch(
              buyerApi.util.updateQueryData('listSavedProperties', args, (draft) => {
                const item = draft.find((i) => i.id === propertyId);
                if (item) item.saved = true;
              })
            )
          ),
          ...buyerApi.util.selectCachedArgsForQuery(state, 'getBuyerPersonalizedFeed').map((args) =>
            dispatch(
              buyerApi.util.updateQueryData('getBuyerPersonalizedFeed', args, (draft) => {
                const item = draft.find((i) => i.id === propertyId);
                if (item) item.saved = true;
              })
            )
          )
        ];
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      }
    }),
    unsaveProperty: builder.mutation<string, { propertyId: number }>({
      query: ({ propertyId }) => ({ url: `buyer/saved/${propertyId}`, method: 'DELETE' }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'SavedProperty', id: 'LIST' },
        { type: 'Property', id: String(arg.propertyId) },
        { type: 'Property', id: 'LIST' },
        { type: 'Property', id: 'FEED' }
      ],
      onQueryStarted: async ({ propertyId }, { dispatch, getState, queryFulfilled }) => {
        const state = getState();
        const patches = [
          ...buyerApi.util.selectCachedArgsForQuery(state, 'listBuyerProperties').map((args) =>
            dispatch(
              buyerApi.util.updateQueryData('listBuyerProperties', args, (draft) => {
                const item = draft.items.find((i) => i.id === propertyId);
                if (item) item.saved = false;
              })
            )
          ),
          ...buyerApi.util.selectCachedArgsForQuery(state, 'listSavedProperties').map((args) =>
            dispatch(
              buyerApi.util.updateQueryData('listSavedProperties', args, (draft) => {
                const item = draft.find((i) => i.id === propertyId);
                if (item) item.saved = false;
              })
            )
          ),
          ...buyerApi.util.selectCachedArgsForQuery(state, 'getBuyerPersonalizedFeed').map((args) =>
            dispatch(
              buyerApi.util.updateQueryData('getBuyerPersonalizedFeed', args, (draft) => {
                const item = draft.find((i) => i.id === propertyId);
                if (item) item.saved = false;
              })
            )
          )
        ];
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      }
    }),

    listBuyerDocuments: builder.query<BuyerPropertyDocument[], { id: number }>({
      query: ({ id }) => ({ url: `buyer/properties/${id}/documents` }),
      transformResponse: (response: unknown) => normalizeDocumentList(response),
      providesTags: (_result, _error, arg) => [{ type: 'PropertyDocument', id: String(arg.id) }]
    }),
    /** A raw file stream, not the envelope — `responseHandler` returns the Blob untouched. */
    getBuyerDocumentFile: builder.query<Blob, BuyerDocumentFileArgs>({
      query: ({ id, docId }) => ({
        url: `buyer/properties/${id}/documents/${docId}/file`,
        responseHandler: (response) => response.blob()
      }),
      keepUnusedDataFor: 0
    })
  })
});

export const {
  useGetBuyerDashboardStatsQuery,
  useGetBuyerDashboardActivityQuery,
  useGetBuyerPersonalizedFeedQuery,
  useGetBuyerDashboardNotificationsQuery,
  useGetBuyerAlertsPreviewQuery,
  useGetBuyerProfileQuery,
  useUpdateBuyerProfileMutation,
  useUpdateBuyerPreferencesMutation,
  useListBuyerPropertiesQuery,
  useGetBuyerPropertyDetailQuery,
  useListSavedPropertiesQuery,
  useSavePropertyMutation,
  useUnsavePropertyMutation,
  useListBuyerDocumentsQuery,
  useLazyGetBuyerDocumentFileQuery
} = buyerApi;
